import PaymentContactInfo from "../components/PaymentContactInfo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useCatalog } from "../contexts/CatalogContext";
import { parseCouponParam, parseProductsParam } from "../lib/checkout-link";
import { CreditCard, PaymentForm } from "react-square-web-payments-sdk";
import { useEffect, useRef, useState } from "react";
import OrderItems from "../components/OrderItems";
import { CatalogObject, Order, Payment } from "square";
import TermsAndAgreementsModal from "../components/TermsAndAgreementsModal";
import {
  calculateShipping,
  calculateTax,
  cleanupFailedCheckout,
  createOrder,
  createPayment,
  getObjects,
  getSquareKeys,
  SquareKeys,
  upsertCustomer,
  validatePromo,
} from "../api";
// Customer/account endpoints aren't part of the legacy `src/api.ts` barrel that
// `../api` resolves to — import them from the domain module, as AccountPage does.
import { getCustomerCards, SavedCard } from "../api/customer";
import CheckoutAccountPanel, {
  SignedInDetails,
} from "../components/CheckoutAccountPanel";
import { sendAdminAlert } from "../api/admin";
import { usePayment } from "../contexts/PaymentContext";
import {
  ChargeVerifyBuyerDetails,
  PaymentRequestOptions,
  TokenResult,
} from "@square/web-sdk";
import ConfirmationModal from "../components/ConfirmationModal";
import PaymentConfirm from "./PaymentConfirm";
import PaymentHeader from "../components/PaymentHeader";
import PaymentSection from "../components/PaymentSection";
import PaymentErrorToast from "../components/PaymentErrorToast";
import Spinner from "../components/Spinner";
import StyledCheckbox from "../components/StyledCheckbox";
import WalletButtons from "../components/WalletButtons";
import {
  FeatureToggle,
  useFeatureToggle,
} from "../contexts/FeatureToggleContext";
import { Tax } from "../types/TaxRate";
import { ShippingRateItem } from "../types/ShippingRate";
import {
  formatDeliveryEstimate,
  isSavedCardExpired,
  isValidPhoneNumber,
  normalizePhoneNumber,
  savedCardExpiry,
  savedCardLabel,
  shippingMethodLabel,
} from "../extensions/helpers";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { getVariationSku } from "../types/OrderItem";
import { trackInitiateCheckout } from "../lib/meta-pixel";

declare global {
  var Square: {
    payments: (
      appId: string,
      locationId: string,
    ) => { card: () => Promise<Card> };
  };
}

type Card = {
  attach: (containerId: string) => Promise<any>;
  tokenize: () => Promise<{ token: string; status: string; errors?: JSON }>;
};

enum PaymentStep {
  ContactInfo = 1,
  Donation = 2,
  Shipping = 3,
  Payment = 4,
  Complete = 5,
}

const PaymentFormPage = () => {
  const { cart, addToCart } = useCart();
  const { items, getVariationBySku, addImages, getImagesByIds } = useCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setErrorMessage } = usePayment();
  const { isFeatureEnabled } = useFeatureToggle();
  const {
    session,
    refreshSession,
    logout,
    sessionExpired,
    clearSessionExpired,
  } = useCustomerAuth();
  const [squareKeys, setSquareKeys] = useState<SquareKeys | null>(null);
  /** At least one wallet button is actually rendered — see WalletButtons. */
  const [walletAvailable, setWalletAvailable] = useState<boolean>(false);

  const isDonationStepEnabled = isFeatureEnabled(
    FeatureToggle.PaymentProcessDonation,
    false, // Disable in Development too
  );

  const [paymentStep, setPaymentStep] = useState<PaymentStep>(
    PaymentStep.ContactInfo,
  );
  const [isOrderSummaryDropdownOpen, setIsOrderSummaryDropdownOpen] =
    useState<boolean>(true);
  const [contactInfo, setContactInfo] = useState<Payment>(() => {
    if (!session) return {};
    // Pre-fill name and email from the logged-in session so the customer
    // doesn't have to retype info they already gave us.
    return {
      buyerEmailAddress: session.email,
      shippingAddress: {
        firstName: session.firstName || undefined,
        lastName: session.lastName || undefined,
        addressLine1: session.savedAddress?.addressLine1 || undefined,
        addressLine2: session.savedAddress?.addressLine2 || undefined,
        locality: session.savedAddress?.locality || undefined,
        administrativeDistrictLevel1:
          session.savedAddress?.administrativeDistrictLevel1 || undefined,
        postalCode: session.savedAddress?.postalCode || undefined,
        country: session.savedAddress?.country || "US",
      } as any,
      billingAddress: {
        firstName: session.firstName || undefined,
        lastName: session.lastName || undefined,
        addressLine1: session.savedAddress?.addressLine1 || undefined,
        addressLine2: session.savedAddress?.addressLine2 || undefined,
        locality: session.savedAddress?.locality || undefined,
        administrativeDistrictLevel1:
          session.savedAddress?.administrativeDistrictLevel1 || undefined,
        postalCode: session.savedAddress?.postalCode || undefined,
        country: session.savedAddress?.country || "US",
      } as any,
    };
  });
  const [phone, setPhone] = useState<string | null>(session?.phone ?? null);

  /**
   * Fill the contact form from an account that was just signed into mid-checkout.
   *
   * The prefill above only runs in the useState initializer — it can't help
   * someone who signs in after landing here, which is now the common path.
   *
   * Fill-if-empty: whatever the buyer has already typed is more current than
   * what Square has on file, and silently rewriting an address they just
   * entered is the kind of thing that loses an order. The email is the one
   * exception — they just proved they own the account's address, and matching
   * it is what links this order to the account. The field stays editable, and
   * upsert-customer won't rename the account if they change it.
   *
   * "Empty" is judged for the WHOLE address at once — name and street together,
   * one all-or-nothing decision. Filling field by field composes one address
   * out of two real ones: a buyer who unchecked "Same as Shipping" and had
   * typed a gift recipient's street and city, then signed in, got OUR account's
   * state and ZIP dropped into the gaps. The result belongs to nobody, looks
   * perfectly plausible on screen, and both Square and Printful accept it
   * without complaint — the parcel is simply undeliverable, or worse,
   * deliverable somewhere wrong.
   *
   * Judging name and street as two SEPARATE blocks left the same seam one level
   * up, and the checkout form walks buyers straight into it: the name fields sit
   * above the street fields, so "typed the recipient's name, then noticed the
   * sign-in panel" is an ordinary way to fill this form. That buyer kept the
   * recipient's name and got the account holder's street composed underneath it
   * — a parcel addressed to the wrong person at the right house. One block
   * cannot produce that. The cost is a buyer who typed only their name and now
   * types their address too; the alternative cost is a misdelivered order.
   */
  const applySignedInPrefill = (details: SignedInDetails) => {
    // They've signed back in, so the notice below has served its purpose. It is
    // local state that nothing else clears — `login()` resets `sessionExpired`
    // for us, but not this — and left standing it tells a signed-in buyer their
    // card "won't be saved this time" while they sit there perfectly able to
    // save one.
    setSessionEndedMidCheckout(false);
    setContactInfo((prev) => {
      // Every field the buyer could have typed into an address block. `country`
      // is deliberately absent: the mount-time initializer defaults it to "US",
      // so counting it would make every block look already-filled and this
      // prefill would never do anything at all.
      const ADDRESS_FIELDS = [
        "firstName",
        "lastName",
        "addressLine1",
        "addressLine2",
        // addressLine3 is a real field on the contact form (PaymentContactInfo
        // renders it) even though SavedAddress has no counterpart to copy in.
        // It still has to count towards "has the buyer typed anything here", or
        // a line-3-only entry reads as empty and the account's name, street,
        // city, state and ZIP get composed around it — the exact half-and-half
        // address this rule exists to prevent.
        "addressLine3",
        "locality",
        "administrativeDistrictLevel1",
        "postalCode",
      ];
      const anyFilled = (block: any) =>
        ADDRESS_FIELDS.some((f) => String(block?.[f] ?? "").trim() !== "");
      // Touched at all → left exactly as typed. Untouched → taken whole from
      // the account. There is no third outcome, which is the point.
      const fillAddress = (existing: any) =>
        anyFilled(existing)
          ? existing
          : {
              ...existing,
              firstName: details.firstName || undefined,
              lastName: details.lastName || undefined,
              addressLine1: details.address?.addressLine1 || undefined,
              addressLine2: details.address?.addressLine2 || undefined,
              locality: details.address?.locality || undefined,
              administrativeDistrictLevel1:
                details.address?.administrativeDistrictLevel1 || undefined,
              postalCode: details.address?.postalCode || undefined,
              country: existing?.country || details.address?.country || "US",
            };
      return {
        ...prev,
        buyerEmailAddress: details.email || prev.buyerEmailAddress,
        shippingAddress: fillAddress(prev.shippingAddress) as any,
        billingAddress: fillAddress(prev.billingAddress) as any,
      };
    });
    setPhone((prev) => prev || details.phone || null);
  };

  /**
   * The mirror image: drop the details that came from the account being signed
   * out of.
   *
   * Both the mount-time initializer above and applySignedInPrefill fill this
   * form from the session, so a sign-out that only clears the session leaves
   * the previous account's email, name and address sitting in the fields. The
   * buyer who clicked "Not you?" has just told us those aren't theirs — and the
   * fields being pre-filled is exactly what makes it easy to walk past them and
   * check out as a guest under someone else's email, which sends the order
   * emails to the wrong inbox and the parcel to the wrong address.
   *
   * Reset to {} rather than to a blank shape: {} is what a guest arriving with
   * no session gets from the initializer, so this lands on the same state.
   * `isContactStepInvalid` then blocks Save & Continue until they retype, so no
   * shipping rate or tax computed for the old address can survive either.
   */
  const clearSignedOutDetails = () => {
    setContactInfo({});
    setPhone(null);
  };

  /**
   * Set when handlePay has to drop a session that no longer resolves to the
   * Square customer its token names — an expired token, or a deleted record.
   *
   * That sign-out is involuntary and happens mid-"Pay", so it can't go through
   * `setErrorMessage`: the order is fine and the confirm modal is about to
   * open, and a red failure toast over a successful checkout says the wrong
   * thing entirely. But it can't be silent either — the buyer's cards and order
   * history vanish from a session they never asked to end, and the card they
   * ticked "save" for won't be saved. A quiet banner is the honest middle.
   */
  const [sessionEndedMidCheckout, setSessionEndedMidCheckout] = useState(false);
  // Every method Printful offers for this destination (STANDARD, and
  // PRINTFUL_FAST when available). `calculatedShipping` is the one selected.
  const [shippingOptions, setShippingOptions] = useState<ShippingRateItem[]>(
    [],
  );
  const [calculatedShipping, setCalculatedShipping] =
    useState<ShippingRateItem | null>(null);
  // Shipping is always its own step so all three step headers (Contact →
  // Shipping → Payment) are visible from the start, before rates are fetched.
  // Fixed numbers — they don't depend on whether rates have loaded yet.
  const shippingStepNumber = isDonationStepEnabled ? 3 : 2;
  const paymentStepNumber = isDonationStepEnabled ? 4 : 3;
  const [isRecalculatingTax, setIsRecalculatingTax] = useState<boolean>(false);
  const [calculatedTax, setCalculatedTax] = useState<Tax | null>(null);
  // const [calculatedOrder, setCalculatedOrder] = useState<Order>(null);
  const [isDonating, setIsDonating] = useState<boolean>(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [confirmationModelOpen, setConfirmationModalOpen] =
    useState<boolean>(false);

  // Promo code state
  const [promoInput, setPromoInput] = useState<string>("");
  const [promoApplied, setPromoApplied] = useState<boolean>(false);
  const [promoDiscountRate, setPromoDiscountRate] = useState<number>(0); // e.g. 0.10 for 10%
  const [promoDiscountCents, setPromoDiscountCents] = useState<number>(0); // fixed $-off codes
  const [promoDisplayName, setPromoDisplayName] = useState<string>("");
  const [promoError, setPromoError] = useState<string | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState<boolean>(false);
  // The last `code|email` pair already checked against the server, so the
  // re-validation effect below fires once per genuinely new pair instead of on
  // every render that touches the email.
  const promoCheckedFor = useRef<string>("");

  // Square Specific State - Validate Form
  const [isFormInvalid, setIsFormInvalid] = useState<boolean>(true);

  // Saved cards ("Save my information for a faster checkout"). Only ever
  // populated for a signed-in customer — a card on file is keyed to a Square
  // customer profile, so there is nothing a guest could be offered.
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  // Which card on file the customer is paying with; null = "use a new card".
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  // Opt-IN, unchecked by default: storing a card needs the buyer's affirmative
  // consent, so this must never start on.
  const [saveNewCard, setSaveNewCard] = useState<boolean>(false);
  // Resolved at pay time and handed to PaymentConfirm, which does the actual
  // save once the order is really placed. Latched from the payment source rather
  // than read from `saveNewCard` later: a wallet or an already-saved card must
  // not trigger a save just because the checkbox happened to be ticked.
  const [saveCardForOrder, setSaveCardForOrder] = useState<boolean>(false);

  // Mobile: once the full Order Summary (which stacks at the top of the page)
  // has scrolled up behind the site header, a compact summary block slides in
  // and pins directly below the header so the running total stays visible.
  const summarySectionRef = useRef<HTMLDivElement>(null);
  const [showStickySummary, setShowStickySummary] = useState<boolean>(false);
  // Tapping the compact bar expands the full summary in place (mirrors the
  // top summary's own expand/collapse), rather than scrolling back up.
  const [isStickySummaryExpanded, setIsStickySummaryExpanded] =
    useState<boolean>(false);
  // The site header is `sticky top-0` (App.css `.header`); the compact bar must
  // sit *below* it, not behind it. Measure it rather than hardcoding 64px, since
  // the logo is `height:auto` and the row height shifts with the viewport.
  const [headerOffset, setHeaderOffset] = useState<number>(64);

  const navigate = useNavigate();

  // Item Total
  const subTotalPrice = cart.reduce(
    (sum, item) =>
      sum +
      Number(item.variation?.itemVariationData?.priceMoney?.amount) *
        item.quantity,
    0,
  );

  // Meta pixel InitiateCheckout — once per visit to the checkout page, and only
  // after the cart has contents. The ?products= deep link hydrates the cart a
  // tick after mount, so firing eagerly would report a checkout with no items.
  const initiateCheckoutSent = useRef<boolean>(false);
  useEffect(() => {
    if (initiateCheckoutSent.current || cart.length === 0) return;
    initiateCheckoutSent.current = true;
    trackInitiateCheckout({
      skus: cart.map((i) => getVariationSku(i.variation)),
      totalCents: subTotalPrice,
      numItems: cart.reduce((n, i) => n + i.quantity, 0),
    });
  }, [cart, subTotalPrice]);

  // Promo discount (applied to item subtotal only, before shipping/tax).
  // Round (not floor) to match how Square rounds the percentage discount on the
  // order — otherwise the displayed estimate is a cent higher than the actual
  // charge (e.g. 10% of $33.99 = $3.399 → Square charges $3.40, not $3.39).
  // Fixed $-off codes carry a cents amount (capped at the subtotal); percentage
  // codes carry a rate. Either way this is only a client-side ESTIMATE — the
  // server re-resolves the code and computes the authoritative charge.
  const discountAmount = !promoApplied
    ? 0
    : promoDiscountCents > 0
      ? Math.min(promoDiscountCents, subTotalPrice)
      : Math.round(subTotalPrice * promoDiscountRate);

  // Effective rate (both code types) for scaling the tax estimate below.
  const effectiveDiscountRate =
    subTotalPrice > 0 ? discountAmount / subTotalPrice : 0;

  // Grand Total After Donation and Shipping
  // If donating, round up to the next whole dollar (subTotalPrice is in cents)
  const totalPriceWithDonation = isDonating
    ? Math.ceil(subTotalPrice / 100) * 100
    : subTotalPrice;

  // Donation Amount
  const donationAmount = totalPriceWithDonation - subTotalPrice;

  // Grand Total After Shipping.
  // Round the dollars→cents conversion: Number("4.49") * 100 is 449.00000000000006
  // in floating point, so round to keep totals as clean integer cents.
  const shippingCents = !calculatedShipping
    ? 0
    : Math.round(Number(calculatedShipping.rate) * 100);
  const totalPrice = totalPriceWithDonation + shippingCents;

  // Tax adjusted for promo discount: sales tax is on the actual selling price
  // (post-discount). When a promo is applied we scale the pre-calculated tax
  // by (1 - discountRate) without triggering a second TaxJar API call.
  const adjustedTaxCents = !calculatedTax
    ? 0
    : promoApplied
      ? Math.round(
          calculatedTax.amount_to_collect * 100 * (1 - effectiveDiscountRate),
        )
      : Math.round(calculatedTax.amount_to_collect * 100);

  // Grand total: discounted subtotal + shipping + adjusted tax
  const grandTotalPrice = totalPrice + adjustedTaxCents - discountAmount;

  // validateForm() only checks that required fields are non-empty, which a phone
  // like "1234" passes — and Square would then reject it (400
  // INVALID_PHONE_NUMBER), failing the upsert and killing the sale. So gate the
  // step on the format too, and catch it here where we can still say why.
  const isContactStepInvalid = isFormInvalid || !isValidPhoneNumber(phone);

  // Track if all required fields are filled
  function validateForm() {
    const requiredFields = document.querySelectorAll(
      "#payment-form-page [required]",
    );

    const allFilled = Array.from(requiredFields).every((field) => {
      const input = field as HTMLInputElement; // Cast to HTMLInputElement
      return (input?.value ?? "").toString().trim() !== ""; // Check if the field is not empty
    });

    setIsFormInvalid(!allFilled); // Enable button only if all fields are filled
  }

  /**
   * Deep-link cart: /payment/checkout?products=<sku>:<qty>,…&coupon=<code>
   *
   * This is the "checkout URL" Meta's shop redirects buyers to (see
   * lib/checkout-link.ts). The buyer arrives with their Meta-side cart in the
   * query string and no cart of their own, so we resolve those SKUs against the
   * catalog and seed the cart before the form renders anything.
   *
   * Three things this has to get right:
   *  1. WAIT FOR THE CATALOG. CatalogContext fetches asynchronously, so on a
   *     cold visit `items` is empty on first render. Returning early WITHOUT
   *     arming the ref means the effect simply runs again once items land — and
   *     the params survive, because we only strip them after a real attempt.
   *  2. RUN ONCE. The ref guards against re-entry (incl. StrictMode's double
   *     invoke), and the params are stripped afterwards so a refresh of the
   *     resulting URL can't add the same items a second time.
   *  3. ADD, DON'T REPLACE. addToCart merges, so a buyer who already had items
   *     keeps them. Never resetCart() here — that would silently destroy a cart
   *     the customer built themselves.
   */
  const deepLinkAppliedRef = useRef<boolean>(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;

    const productsParam = searchParams.get("products");
    const couponParam = searchParams.get("coupon");
    if (!productsParam && !couponParam) return;

    const requested = parseProductsParam(productsParam);
    // Catalog not loaded yet — try again on the render that fills `items`.
    if (requested.length > 0 && (items?.length ?? 0) === 0) return;

    deepLinkAppliedRef.current = true;

    const matched: { item: CatalogObject; variation: CatalogObject }[] = [];
    for (const { sku, quantity } of requested) {
      const match = getVariationBySku(sku);
      // An unresolvable SKU (renamed/removed product, stale Meta feed) is
      // skipped rather than failing the whole link.
      if (!match) continue;
      matched.push(match);
      addToCart(
        { product: match.item, variation: match.variation, quantity: 0 },
        quantity,
      );
    }

    // Warm the catalog image cache for what we just added. OrderItems resolves
    // each thumbnail from the VARIATION's image id, but CatalogProvider never
    // fetches IMAGE objects itself — the cache fills lazily as a visitor browses
    // product pages. A deep-linked buyer has browsed nothing, so without this
    // the cart renders every line item imageless.
    const neededImageIds = new Set<string>();
    for (const m of matched) {
      for (const id of m.item.itemData?.imageIds ?? []) neededImageIds.add(id);
      for (const id of m.variation.itemVariationData?.imageIds ?? [])
        neededImageIds.add(id);
    }
    const missingImageIds = Array.from(neededImageIds).filter(
      (id) => getImagesByIds([id]).length === 0,
    );
    if (missingImageIds.length > 0) {
      // Fire-and-forget: a missing thumbnail must never block checkout.
      void getObjects(missingImageIds)
        .then((res) => {
          if (res?.data && !res.error) addImages(res.data.objects ?? []);
        })
        .catch(() => {});
    }

    const coupon = parseCouponParam(couponParam);
    if (coupon) {
      // A deep-linked coupon (e.g. Meta's shop checkout URL) is applied
      // SILENTLY: the buyer never typed it, so if it doesn't validate we drop
      // it with no prefill and no "invalid code" error. handleApplyPromo fills
      // the input itself only on success. This keeps a stray/placeholder value
      // in an inbound link (a Meta URL configured with a bogus `coupon=`) from
      // parking junk in the promo field and confusing the buyer.
      void handleApplyPromo(coupon, { silent: true });
    }

    // Strip the params so a refresh doesn't re-apply them.
    const next = new URLSearchParams(searchParams);
    next.delete("products");
    next.delete("coupon");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items]);

  // codeOverride lets the deep-link effect apply a ?coupon= code immediately,
  // without waiting a render for setPromoInput to land in state.
  //
  // opts.silent suppresses both error surfaces AND the input prefill, so an
  // invalid deep-linked code leaves no trace. A code applied via codeOverride
  // is written into the input only once it validates (below), never up-front.
  /** Drop an applied promo and its estimate (Remove button, and re-validation). */
  const clearPromo = (reason?: string) => {
    setPromoApplied(false);
    setPromoDiscountRate(0);
    setPromoDiscountCents(0);
    setPromoInput("");
    setPromoDisplayName("");
    setPromoError(reason ?? null);
    promoCheckedFor.current = "";
  };

  const handleApplyPromo = async (
    codeOverride?: string,
    opts?: { silent?: boolean },
  ) => {
    const code = (codeOverride ?? promoInput).trim();
    if (!code) return;
    setPromoError(null);
    setIsValidatingPromo(true);
    try {
      const email = contactInfo.buyerEmailAddress ?? undefined;
      promoCheckedFor.current = `${code.toUpperCase()}|${email ?? ""}`;
      const result = await validatePromo(code, email);
      if (result?.error) {
        if (!opts?.silent)
          setPromoError("Could not validate promo code. Please try again.");
        return;
      }
      if (result?.data?.valid) {
        // Reflect a deep-linked code in the (now disabled) input on success.
        if (codeOverride) setPromoInput(code.toUpperCase());
        setPromoApplied(true);
        if (result.data.discountType === "fixed") {
          setPromoDiscountCents(result.data.discountAmountCents ?? 0);
          setPromoDiscountRate(0);
        } else {
          setPromoDiscountRate(
            parseFloat(result.data.discountPercentage ?? "0") / 100,
          );
          setPromoDiscountCents(0);
        }
        setPromoDisplayName(result.data.displayName ?? code.toUpperCase());
      } else {
        if (!opts?.silent)
          setPromoError(result?.data?.reason ?? "Invalid promo code.");
      }
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // A code with a PER-CUSTOMER limit can only be judged once we know who the
  // buyer is, and the promo box lives in the order summary — reachable (and
  // deep-linkable via ?coupon=) long before the Contact step is filled in. So
  // re-check an applied code whenever the email changes: without this, a buyer
  // who had already used the code would see the discount in the summary and
  // then be charged full price, because create-order re-resolves with the real
  // email and silently drops a code it refuses. Server-side stays
  // authoritative; this only keeps the displayed total honest.
  useEffect(() => {
    const email = contactInfo.buyerEmailAddress?.trim() ?? "";
    const code = promoInput.trim().toUpperCase();
    // Two guards, both because the email field updates on EVERY keystroke
    // (PaymentContactInfo sets state from onChange, not onBlur): only a
    // plausibly complete address is worth asking about, and only once typing
    // pauses. Without them a buyer typing their address fires one
    // validate-promo — two Blobs reads apiece — per character.
    if (!promoApplied || !code) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const pair = `${code}|${email}`;
    if (promoCheckedFor.current === pair) return;
    let canceled = false;
    const timer = setTimeout(() => {
      promoCheckedFor.current = pair;
      void (async () => {
        const result = await validatePromo(code, email);
        // A transport failure leaves the code applied — create-order decides the
        // real charge either way, and yanking a discount over a flaky request
        // would be worse than a briefly optimistic estimate.
        if (canceled || result?.error || result?.data?.valid !== false) return;
        clearPromo(result.data.reason ?? "This promo code is no longer valid.");
      })();
    }, 600);
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactInfo.buyerEmailAddress, promoApplied, promoInput]);

  /**
   * Refresh the sales-tax estimate for a given shipping rate. Tax is a function
   * of the shipping charge, so this must re-run whenever the customer picks a
   * different method. Non-fatal by design — see the catch note below.
   *
   * Sends `totalPriceWithDonation` (shipping-EXCLUSIVE) as the total, never
   * `totalPrice`. calculate-tax computes `taxableAmount = total + shipping`
   * itself, so a total that already contains a rate double-counts shipping —
   * and because state updates are async, `totalPrice` would also carry the
   * *previous* selection when switching methods, lagging a click behind.
   */
  const refreshTaxEstimate = async (rate: ShippingRateItem) => {
    try {
      // Use the real calculate-tax endpoint in all environments (including
      // development) so the dev estimate matches the authoritative server-side
      // calculation in create-order.
      const calculatedTax = await calculateTax(
        contactInfo,
        totalPriceWithDonation / 100,
        rate.rate,
      );
      if (calculatedTax && !!calculatedTax.data && !calculatedTax.error) {
        setCalculatedTax(calculatedTax.data.tax);
      } else {
        // Non-fatal: this is only the DISPLAY estimate. The authoritative
        // tax is computed server-side in create-order, so don't scare the
        // customer with an error toast over a failed estimate.
        console.error("Calculate Tax failed:", calculatedTax?.error);
      }
    } catch (error) {
      console.error("Calculate Tax failed:", error);
    }
  };

  /** Customer picked a different shipping method — re-estimate tax for it. */
  const handleSelectShipping = async (rate: ShippingRateItem) => {
    if (rate.id === calculatedShipping?.id) return;
    setCalculatedShipping(rate);
    setIsRecalculatingTax(true);
    await refreshTaxEstimate(rate);
    setIsRecalculatingTax(false);
  };

  const handleCalculateOrder = async (): Promise<ShippingRateItem[] | null> => {
    // 1. Validate that we don't have to do it
    if (cart.length <= 0 || subTotalPrice <= 0) {
      return null;
    }

    try {
      // 2. Calculate Shipping — Printful returns every method available for the
      // destination. Express (PRINTFUL_FAST) only comes back when phone is sent.
      const calculatedShipping = await calculateShipping(
        cart,
        contactInfo,
        normalizePhoneNumber(phone),
      );
      if (
        calculatedShipping &&
        !!calculatedShipping.data?.length &&
        !calculatedShipping.error
      ) {
        // Cheapest first, so the default selection is never a surprise upsell.
        const options = [...calculatedShipping.data].sort(
          (a, b) => Number(a.rate) - Number(b.rate),
        );
        setShippingOptions(options);
        setCalculatedShipping(options[0]);
        // 3. Calculate Sales Tax for the default (cheapest) method.
        await refreshTaxEstimate(options[0]);
        // Return the rates so the caller can advance to the Shipping step; null
        // (below) signals the fetch failed and it should skip straight to Payment.
        return options;
      } else {
        console.error("Calculate Shipping failed:", calculatedShipping);
        setErrorMessage(
          `Calculate Shipping Failed${
            calculatedShipping?.error ? `: ${calculatedShipping.error}` : ""
          }`,
        );
      }
    } catch (error) {
      console.error("Calculate Shipping failed:", error);
      setErrorMessage(`Calculate Shipping Failed: ${error}`);
    }
    return null;
  };

  /**
   * ContactInfo (or Donation) → next step. Runs the shipping/tax calc, then
   * routes to the Shipping-method step whenever at least one rate came back
   * (Shipping is always its own step, so all three headers show from the start),
   * and only skips straight to Payment when the rate fetch failed.
   */
  const handleAdvanceToShippingOrPayment = async () => {
    setIsCalculating(true);
    const options = await handleCalculateOrder();
    setIsCalculating(false);
    // Land on the Shipping step whenever we have at least one rate; only skip
    // straight to Payment if the rate fetch failed (options === null).
    setPaymentStep(
      options && options.length >= 1
        ? PaymentStep.Shipping
        : PaymentStep.Payment,
    );
  };

  const createPaymentRequest = (): PaymentRequestOptions => {
    const shippingRate = !calculatedShipping
      ? 0
      : Number(calculatedShipping.rate);

    return {
      countryCode: "US",
      currencyCode: "USD",
      // We already collected the shipping address in Step 1; do NOT request it
      // again from Apple/Google Pay (that would allow changing it without
      // recalculating shipping/tax).
      requestShippingContact: false,
      requestBillingContact: true,
      // Pre-populate the shipping address so Apple Pay shows where the order is going.
      shippingContact: contactInfo.shippingAddress
        ? {
            givenName:
              (contactInfo.shippingAddress as any).firstName ?? undefined,
            familyName:
              (contactInfo.shippingAddress as any).lastName ?? undefined,
            addressLines: [
              contactInfo.shippingAddress.addressLine1,
              contactInfo.shippingAddress.addressLine2,
            ].filter(Boolean) as string[],
            city: contactInfo.shippingAddress.locality ?? undefined,
            state:
              contactInfo.shippingAddress.administrativeDistrictLevel1 ??
              undefined,
            postalCode: contactInfo.shippingAddress.postalCode ?? undefined,
            countryCode: contactInfo.shippingAddress.country,
            email: contactInfo.buyerEmailAddress,
            phone: phone ?? undefined,
          }
        : undefined,
      lineItems: [
        ...cart.map((item) => ({
          label: `${item.product?.itemData?.name ?? "Item"} × ${item.quantity}`,
          amount: (
            (Number(item.variation?.itemVariationData?.priceMoney?.amount) *
              item.quantity) /
            100
          )
            .toFixed(2)
            .toString(),
        })),
        ...(discountAmount > 0
          ? [
              {
                label: promoDisplayName || "Promo Discount",
                amount: `-${(discountAmount / 100).toFixed(2)}`,
              },
            ]
          : []),
        // Include the donation as its own line so the wallet's line items sum
        // to the total (the total already includes the rounded-up donation).
        ...(donationAmount > 0
          ? [
              {
                label: "Donation",
                amount: (donationAmount / 100).toFixed(2),
              },
            ]
          : []),
      ],
      shippingLineItems:
        shippingRate > 0
          ? [
              {
                label: calculatedShipping?.name ?? "Shipping",
                amount: shippingRate.toFixed(2).toString(),
              },
            ]
          : [],
      // Use the promo-adjusted tax so the breakdown reconciles with the total.
      taxLineItems:
        adjustedTaxCents > 0
          ? [
              {
                label: "Sales Tax",
                amount: (adjustedTaxCents / 100).toFixed(2),
              },
            ]
          : [],
      total: {
        amount: (grandTotalPrice / 100).toFixed(2).toString(),
        label: "Americana & Company",
      },
    };
  };

  const getVerificationDetails = (): ChargeVerifyBuyerDetails => {
    return {
      amount: (grandTotalPrice / 100).toFixed(2).toString(),
      currencyCode: "USD",
      intent: "CHARGE",
      billingContact: {
        givenName: contactInfo.billingAddress?.firstName?.trim(),
        familyName: contactInfo.billingAddress?.lastName?.trim(),
        email: contactInfo.buyerEmailAddress?.trim(),
        addressLines: [
          contactInfo.billingAddress?.addressLine1?.trim(),
          contactInfo.billingAddress?.addressLine2?.trim(),
          contactInfo.billingAddress?.addressLine3?.trim(),
        ].filter((x): x is string => Boolean(x)),
        city: contactInfo.billingAddress?.locality?.trim(),
        state: contactInfo.billingAddress?.administrativeDistrictLevel1?.trim(),
        postalCode: contactInfo.billingAddress?.postalCode?.trim(),
        countryCode: contactInfo.billingAddress?.country?.trim(),
      },
    };
  };

  /**
   * Run the checkout: order → authorize → confirm modal.
   *
   * @param token         Tokenized card / wallet result. Null on the saved-card
   *                      path, where there is nothing to tokenize.
   * @param verifiedBuyer SCA verification token, when the SDK produced one.
   * @param savedCardId   Charge this card on file instead of a token. The server
   *                      re-verifies that it belongs to the signed-in customer.
   */
  const handlePay = async (
    token: TokenResult | null,
    verifiedBuyer?: { token: string } | null,
    savedCardId?: string,
  ) => {
    if (subTotalPrice <= 0) {
      console.error("Cannot create order, no items in cart!");
      setErrorMessage("Cannot create order, no items in cart!");
      return;
    }

    if (!savedCardId && (!token || token.status !== "OK" || token.errors)) {
      const errMsg = Array.isArray(token?.errors)
        ? token!.errors.map((e: any) => e.message).join(". ")
        : typeof token?.errors === "string"
          ? token.errors
          : "Tokenization failed";
      console.error("Tokenization errors:", token?.errors);
      setErrorMessage(`Cannot verify the payment method: ${errMsg}`);
      return;
    }

    // Clear any error from a prior attempt so the toast reflects this one.
    setErrorMessage(null);
    setIsSubmitting(true);

    // Hoisted so the catch can compensate for an order this attempt created.
    // `createdOrder` is set the moment Square has an order; `settled` flips once
    // the order's fate is decided (payment authorized, or already cleaned up),
    // and is what stops the catch from cancelling a good order.
    let createdOrder: Order | null = null;
    let settled = false;
    // The email create-order stamped on the order — getOrder is ownership-gated,
    // so the cleanup below needs it to read back its own order as a guest.
    let orderEmail: string | undefined;

    try {
      // Merge billing contact from Apple Pay / Google Pay token details.
      // Digital wallet payment methods return verified billing info in
      // token.details.billing; this takes precedence over what the user typed
      // in Step 1 for the billing ADDRESS (the wallet's copy is the one the
      // card issuer will match on AVS).
      //
      // The EMAIL is deliberately the other way round: whatever the buyer typed
      // at Contact wins, and the wallet's address only fills a blank. It is not
      // really a billing field here — it's the identity of the order. It's the
      // only address the confirmation, shipping and tracking mail is sent to
      // (send-email-order posts to `contactInfo.buyerEmailAddress`), and it's
      // the ownership key a guest is checked against by `get-order` on the
      // track / cancel / return pages.
      //
      // A wallet supplies the email of the WALLET ACCOUNT, which routinely
      // isn't the one the buyer just typed. Letting it win sent the whole
      // order's mail to an inbox the buyer never named and then refused them
      // their own order lookup under the address they did name — reported as
      // "I paid with Google Pay and never got a confirmation email".
      const tokenBilling = token?.details?.billing;
      const effectiveContactInfo: Payment = {
        ...contactInfo,
        buyerEmailAddress: contactInfo.buyerEmailAddress || tokenBilling?.email,
        billingAddress: {
          ...contactInfo.billingAddress,
          firstName:
            tokenBilling?.givenName ||
            (contactInfo.billingAddress as any)?.firstName,
          lastName:
            tokenBilling?.familyName ||
            (contactInfo.billingAddress as any)?.lastName,
          addressLine1:
            tokenBilling?.addressLines?.[0] ||
            contactInfo.billingAddress?.addressLine1,
          addressLine2:
            tokenBilling?.addressLines?.[1] ||
            contactInfo.billingAddress?.addressLine2,
          locality: tokenBilling?.city || contactInfo.billingAddress?.locality,
          administrativeDistrictLevel1:
            tokenBilling?.state ||
            contactInfo.billingAddress?.administrativeDistrictLevel1,
          postalCode:
            tokenBilling?.postalCode || contactInfo.billingAddress?.postalCode,
          country:
            tokenBilling?.countryCode || contactInfo.billingAddress?.country,
        },
      };
      // Normalize before it reaches Square (which validates the format) and
      // Printful/the carrier. A wallet-supplied phone is already E.164.
      const effectivePhone = normalizePhoneNumber(tokenBilling?.phone || phone);

      // Upsert Customer. This runs even when signed in — an account created
      // during this very checkout (or by an OTP sign-in) is a bare Square
      // record holding nothing but an email address, so skipping the upsert
      // would leave it with no name, address or phone and there'd be nothing to
      // prefill next time, which is the entire point of having the account.
      //
      // The auth token makes the server target the session's own customer id
      // instead of searching by the typed email, so the order, the payment and
      // any saved card all land on one record. See upsert-customer.js.
      let customer: { id?: string | null };
      const customerResponse = await upsertCustomer(
        effectiveContactInfo,
        effectivePhone ?? undefined,
        session?.authToken,
      );
      if (!customerResponse?.data || customerResponse?.error) {
        // A signed-in buyer has a customer id we already trust — a failed
        // enrichment costs a prefill, not the sale. Guests have no fallback.
        if (!session?.customerId) {
          throw new Error(
            `Upsert Customer Failed: ${customerResponse?.error ?? "unknown"}`,
          );
        }
        console.error("Upsert Customer failed; using session customer id");
        customer = { id: session.customerId };
      } else {
        customer = customerResponse.data;

        // Carry the enrichment back into the session, so the account this
        // checkout just filled in prefills the NEXT one without waiting for a
        // fresh sign-in — the session object is what the mount-time initializer
        // reads, and it's still the bare record OTP handed us.
        //
        // Copy from the SERVER's record, never from what was typed. The server
        // fills blanks only on the buyer's own account (see upsert-customer.js),
        // so the response already reflects a gift order leaving the profile
        // alone; mirroring `effectiveContactInfo` instead would put the
        // recipient's name and address back into the session and prefill them
        // next time — exactly what the server-side rule exists to prevent.
        if (session?.customerId && customer.id === session.customerId) {
          const record = customerResponse.data;
          refreshSession({
            firstName: record.givenName ?? session.firstName,
            lastName: record.familyName ?? session.lastName,
            phone: record.phoneNumber ?? session.phone,
            // Square types every address field as `string | null | undefined`;
            // the session stores plain optionals, so null becomes undefined.
            ...(record.address?.addressLine1
              ? {
                  savedAddress: {
                    addressLine1: record.address.addressLine1,
                    addressLine2: record.address.addressLine2 ?? undefined,
                    locality: record.address.locality ?? undefined,
                    administrativeDistrictLevel1:
                      record.address.administrativeDistrictLevel1 ?? undefined,
                    postalCode: record.address.postalCode ?? undefined,
                    country: record.address.country ?? undefined,
                  },
                }
              : {}),
          });
        } else if (session?.customerId && customer.id !== session.customerId) {
          // The upsert landed somewhere other than the session's account. Two
          // different causes reach here, and the session is unusable either way:
          //
          //   - The token's customer is GONE (deleted in the Square dashboard,
          //     or a session carried across a sandbox/production switch), so the
          //     server rebuilt the record from the buyer's email.
          //   - The token has EXPIRED. `isSessionExpired` in CustomerAuthContext
          //     now drops a lapsed session before it can get this far, so this
          //     is the rare residual: a device whose clock runs behind passes
          //     the client check and still fails the server's. verifyAuthHeader
          //     throws, the request falls through to the guest email search, and
          //     that can resolve a different record whenever Square holds
          //     near-duplicates (customer-send-otp mints minimal ones routinely).
          //
          // Either way the JWT no longer names the record this order landed on,
          // so this session can't read its own orders, cards or profile — and
          // nothing repairs it on its own, because the id is baked into the
          // token. Every later checkout would repeat this same fallback. The
          // banner says the account couldn't be VERIFIED rather than naming
          // either cause — "expired" is wrong when the record was deleted, and
          // "we couldn't find your account" is wrong when it's a clock skew.
          //
          // `session.customerId` must be TRUTHY to reach here: a session can
          // legitimately carry a null one (customer-send-otp treats a failed
          // Square createCustomer as non-fatal and signs the token without an
          // id), and that is not a dead account — the upsert resolving to a
          // real record is the expected outcome there, not a mismatch. Signing
          // that buyer out mid-Pay would be gratuitous; their card save is
          // already suppressed below, because the token still can't prove it
          // owns the record the payment landed on.
          //
          // Drop it. This order is unaffected (it's already bound to the new
          // record), the card save is skipped just below, and the buyer signs
          // in again to get a token for the record that now exists.
          console.error(
            "Session no longer resolves to its Square customer; signing out",
          );
          logout();
          setSessionEndedMidCheckout(true);
        }
      }

      // Compute shipping and tax in whole cents so the order total matches
      // the payment amount exactly (Square enforces ORDER_TOTAL_MISMATCH otherwise).
      const shippingAmountCents = Math.round(
        Number(calculatedShipping?.rate ?? 0) * 100,
      );
      // Use the promo-adjusted tax amount (scaled by (1 - discountRate) when
      // a promo is active) so the order service charge matches the UI display.
      const taxAmountCents = adjustedTaxCents;

      // Per-attempt idempotency key. Each genuine "Pay" click gets a fresh key,
      // so a customer whose first attempt fails (declined card, SCA failure) can
      // retry the same cart instead of colliding on Square's idempotency key
      // (IDEMPOTENCY_KEY_REUSED). Threaded to both create-order and
      // create-payment so the retry produces a fresh order + payment attempt.
      const attemptIdempotencyKey = crypto.randomUUID();

      // Last word on the promo, before anything is created. create-order
      // re-resolves the code against the email it actually stamps on the order
      // and silently DROPS one it refuses (a per-customer limit already spent,
      // a window that closed) — the charge is then Square's authoritative,
      // undiscounted total, taken from a buyer who was just shown a discounted
      // one. The effect above covers a typed email, but not a wallet supplying
      // `buyerEmailAddress` for the first time right here, nor a Pay click that
      // beat the request. Abort rather than throw: nothing exists yet to clean
      // up, and "you've already used this code" is a normal answer, not a
      // checkout failure worth alerting an admin about.
      const promoCodeForOrder = promoApplied
        ? promoInput.trim().toUpperCase()
        : undefined;
      if (promoCodeForOrder) {
        const recheck = await validatePromo(
          promoCodeForOrder,
          effectiveContactInfo.buyerEmailAddress ?? undefined,
        );
        // Only a definite refusal stops the sale; a transport failure leaves
        // the decision to create-order, exactly as before.
        if (recheck?.data?.valid === false) {
          const reason =
            recheck.data.reason ?? "That promo code is no longer valid.";
          clearPromo(reason);
          setErrorMessage(
            `${reason} Your total has been updated — please review it and pay again.`,
          );
          return;
        }
      }

      // Create Order (applies promo discount at the Square order level when present)
      const orderResponse = await createOrder(
        customer.id!,
        cart,
        effectiveContactInfo,
        donationAmount,
        shippingAmountCents,
        taxAmountCents > 0 ? taxAmountCents : 0,
        promoCodeForOrder,
        attemptIdempotencyKey,
      );

      if (!orderResponse?.data || orderResponse?.error) {
        throw new Error(`Create Order Failed: ${orderResponse?.error ?? ""}`);
      }

      const order = orderResponse.data;
      createdOrder = order;
      orderEmail = effectiveContactInfo.buyerEmailAddress ?? undefined;

      // Did the discount actually land? create-order re-resolves the code and
      // silently omits the discount from the order when it refuses one — and
      // the payment amount below is read from THAT order, so a refusal we don't
      // notice charges the buyer the undiscounted total they were never shown.
      //
      // The recheck above closes the ordinary paths, but not the one where
      // validate-promo DEGRADED: over its per-IP budget it answers without the
      // buyer identity, so a spent per-customer limit reads as valid there and
      // is refused only here. A shared egress IP (carrier CGNAT, an office)
      // reaches that budget with no abuse at all.
      //
      // `discounts` is the exact signal — present for all three application
      // paths (catalog AMERICA250, ad-hoc fixed, ad-hoc percentage) and absent
      // only when no promo resolved. Comparing totals instead would be brittle:
      // sales tax is computed server-side, so the client's estimate is not
      // authoritative. Cancel the order first — it exists now, and leaving it
      // OPEN would sit there until cleanup-orders sweeps it.
      if (promoCodeForOrder && !(order.discounts ?? []).length) {
        await cleanupFailedCheckout(order, {
          authToken: session?.authToken,
          email: orderEmail,
        });
        settled = true;
        clearPromo();
        setErrorMessage(
          "That promo code could not be applied to this order. Your total has been updated — please review it and pay again.",
        );
        return;
      }

      // Use Square's authoritative order total as the payment amount.
      // This accounts for the exact per-item discount distribution that Square
      // calculates and avoids ORDER_TOTAL_MISMATCH errors.
      const amountInCents = Number(
        order.totalMoney?.amount ?? Math.round(grandTotalPrice),
      );

      // Create Payment. On the saved-card path there's no token — the card id
      // goes up instead, with the auth token that lets the server prove the card
      // is this customer's.
      const paymentResponse = await createPayment(
        token?.token ?? "",
        amountInCents,
        order.id!,
        customer.id!,
        effectiveContactInfo,
        verifiedBuyer?.token,
        attemptIdempotencyKey,
        savedCardId
          ? { savedCardId, authToken: session?.authToken }
          : undefined,
      );

      if (!paymentResponse?.data || paymentResponse?.error) {
        // A decline and a lost response are indistinguishable here: createPayment
        // swallows fetch rejections and returns { error }. So Square may have
        // authorized the card. Void anything live before closing the order,
        // rather than waiting 30 min for cleanup-orders.mjs.
        await cleanupFailedCheckout(order, {
          authToken: session?.authToken,
          email: orderEmail,
        });
        settled = true;
        throw new Error(
          `Capture Payment Failed: ${paymentResponse?.error ?? ""}`,
        );
      }

      // Payment is AUTHORIZED. From here the order is the customer's; the
      // confirm modal (PaymentConfirm) owns any further cleanup.
      settled = true;

      const payment = paymentResponse.data;

      // Set Order and Payment Info and open confirmation.
      // Persist the effective (possibly Apple Pay-merged) contact info so
      // PaymentConfirm and the Printful fulfillment call use the right data.
      setContactInfo(effectiveContactInfo);
      setPhone(effectivePhone ?? null);
      // Only a card typed into the card form can be saved: a wallet token is
      // device-bound (there's nothing to store), and a card on file is already
      // stored. `method` is the SDK's own record of what produced the token.
      //
      // The id check is the stale-session case: if the token's customer no
      // longer exists, upsert-customer creates a fresh record and the payment
      // lands on it, but the JWT still names the old one — save-customer-card
      // compares the two and would refuse. Don't fire a request that can't win.
      setSaveCardForOrder(
        !savedCardId &&
          token?.details?.method === "Card" &&
          saveNewCard &&
          !!session?.authToken &&
          customer.id === session?.customerId,
      );
      setOrder(order);
      setPayment(payment);
      // The expiry notice has outlived its usefulness the moment the buyer
      // reaches the confirm step — they've decided how to check out, with or
      // without the account. Left set it would greet them again on the next
      // order, since the flag lives in the auth context and this page doesn't
      // own it. (`sessionEndedMidCheckout` deliberately survives: it still has
      // to reach the receipt as `accountNotice`.)
      clearSessionExpired();
      setConfirmationModalOpen(true);
    } catch (error: any) {
      console.error("Payment flow failed:", error);

      // An order this attempt created is now orphaned OPEN, and create-payment
      // may have authorized the card without us seeing the response. Void and
      // close it rather than leaving it for the daily cleanup-orders sweep.
      if (createdOrder && !settled) {
        await cleanupFailedCheckout(createdOrder, {
          authToken: session?.authToken,
          email: orderEmail,
        });
      }

      void sendAdminAlert(
        `⚠️ Checkout Flow Failed`,
        "An unexpected error occurred during the checkout flow. The order or payment may be in an incomplete state.",
        {
          Error: String(error?.message ?? error),
        },
      );
      setErrorMessage(error?.message ?? String(error));
    } finally {
      // Always ensure submitting state is cleared
      setIsSubmitting(false);
    }
  };

  // Re-validate whenever contactInfo changes — this covers the case where an
  // address is selected from the autocomplete dropdown, which updates React
  // state directly without dispatching native input events (so the DOM
  // listener below would never fire for that interaction).
  //
  // `phone` MUST be a dependency too. It lives outside contactInfo but the field
  // is `required`, so validateForm() counts it — and without this, filling the
  // address before the phone latches isFormInvalid true forever and Save &
  // Continue never enables.
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactInfo, phone]);

  // A stale error from a previous visit shouldn't greet a fresh checkout.
  useEffect(() => {
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the header height current so the bar pins just under it (not behind).
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector(".header") as HTMLElement | null;
      if (header) setHeaderOffset(header.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Reveal the compact summary bar once the full summary has scrolled up behind
  // the header, and hide it again on the way back up. `rootMargin` insets the
  // top by the header height so the reveal fires exactly when the summary
  // disappears under the header, not when it clears the very top of the screen.
  // The bar is a fixed overlay, so toggling it never reflows the summary — the
  // observed element's geometry is stable, which keeps this jitter-free.
  useEffect(() => {
    const el = summarySectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const next =
          !entry.isIntersecting && entry.boundingClientRect.top < headerOffset;
        setShowStickySummary(next);
        // Don't leave the panel expanded once the bar itself slides away.
        if (!next) setIsStickySummaryExpanded(false);
      },
      { threshold: 0, rootMargin: `-${headerOffset}px 0px 0px 0px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [headerOffset]);

  // Validation is driven entirely by the contactInfo effect above.
  // Native DOM event listeners were removed — they fired validateForm() before
  // React had committed the new controlled-input value, causing a render with
  // stale state that cleared the typed character and left the button in the
  // wrong enabled/disabled state.

  useEffect(() => {
    const getKeys = async () => {
      const keysResponse = await getSquareKeys();
      if (keysResponse && !!keysResponse.data && !keysResponse.error) {
        setSquareKeys(keysResponse.data);
      } else {
        console.error("Error getting keys.", keysResponse?.error);
      }
    };

    getKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the signed-in customer's cards on file and preselect the most recent
  // one, so the common case is: land on Payment, press Pay.
  //
  // Expired cards are filtered out rather than shown greyed: Square keeps
  // returning them from ListCards forever, and offering one only produces a
  // decline at the worst possible moment. The customer just gets the card form.
  useEffect(() => {
    const authToken = session?.authToken;
    // Signing OUT mid-checkout is reachable now that the Contact step carries
    // an account panel ("Not you? … check out as a guest"). Leaving the list
    // behind would offer a card the buyer can no longer pay with: handlePay
    // sends `savedCardId` with no auth header, and create-payment rejects it.
    if (!authToken) {
      setSavedCards([]);
      setSelectedCardId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await getCustomerCards(authToken);
      if (cancelled || !res?.data || res.error) return;
      const usable = res.data.filter(
        (card) => card.enabled && !isSavedCardExpired(card),
      );
      setSavedCards(usable);
      // Newest first (the server asks Square for DESC), so [0] is the card they
      // most recently chose to save.
      if (usable.length > 0) setSelectedCardId(usable[0].id);
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.authToken]);

  const buttonProps = isFormInvalid
    ? {
        css: {
          "pointer-events": "none",
          opacity: 0.6,
          cursor: "not-allowed",
          content: "center",
          width: "100%",
          height: "40px",
          backgroundColor: "#4caf50",
          fontSize: "14px",
          verticalAlign: "middle",
          padding: "0 0 1px 0",
          color: "#fff",
          "&:hover": {
            backgroundColor: "#2b6e2d",
          },
        },
      }
    : {
        css: {
          content: "center",
          width: "100%",
          height: "40px",
          backgroundColor: "#4caf50",
          fontSize: "14px",
          verticalAlign: "middle",
          padding: "0 0 1px 0",
          color: "#fff",
          "&:hover": {
            backgroundColor: "#2b6e2d",
          },
        },
      };

  // The expandable body of the order summary — items, promo, pricing, total.
  // Rendered both in the top summary column and inside the mobile compact bar's
  // drop-down, so it lives in one place. (The two promo inputs it can mount at
  // once are both bound to the same state, so they stay in sync.)
  const orderSummaryDetails = (
    <>
      {/* Order Details */}
      <OrderItems isCheckout />

      {/* Promo Code */}
      <div className="mt-4">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Promo code"
            value={promoInput}
            onChange={(e) => {
              setPromoInput(e.target.value);
              if (promoError) setPromoError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!promoApplied && !isValidatingPromo) {
                  handleApplyPromo();
                }
              }
            }}
            disabled={promoApplied}
            autoComplete="off"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-400"
          />
          {!promoApplied ? (
            <button
              type="button"
              // Arrow, not a bare reference: handleApplyPromo takes an optional
              // code override, and passing it directly would hand it the click
              // event as the promo code.
              onClick={() => handleApplyPromo()}
              disabled={!promoInput.trim() || isValidatingPromo}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
            >
              {isValidatingPromo ? "Checking…" : "Apply"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => clearPromo()}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Remove
            </button>
          )}
        </div>
        {promoError && (
          <p className="text-red-500 text-xs mt-1">{promoError}</p>
        )}
        {promoApplied && (
          <p className="text-green-600 text-xs mt-1 font-medium">
            ✓ <span translate="no">{promoDisplayName}</span> applied
          </p>
        )}
      </div>

      {/* Shipping method is now chosen in its own checkout step (see the
          Shipping step in the form column) — on mobile the order summary sits
          far above the flow, so a picker buried here got scrolled past and never
          selected. The chosen rate still appears in the pricing summary below. */}

      {/* Order Pricing Summary */}
      <div className="mt-4">
        <p className="payment-summary-item">
          <span>Subtotal</span>{" "}
          <span>${(subTotalPrice / 100).toFixed(2).toString()}</span>
        </p>
        {promoApplied && discountAmount > 0 && (
          <p className="payment-summary-item text-green-600">
            <span translate="no">{promoDisplayName}</span>{" "}
            <span>-${(discountAmount / 100).toFixed(2)}</span>
          </p>
        )}
        {/* Before an address is entered there's no Printful quote yet, so a
            bare "$0" reads as free shipping. Say what's actually pending, in
            the same slot the rate (and, below, the delivery estimate) lands in
            once the quote comes back. */}
        <p className="payment-summary-item">
          <span>Estimated Shipping</span>{" "}
          {calculatedShipping ? (
            <span>${Number(calculatedShipping.rate).toFixed(2)}</span>
          ) : (
            <span className="text-gray-500 text-right">
              Calculated after shipping address
            </span>
          )}
        </p>
        {/* The picker already shows a per-option estimate — only repeat it here
            when there was no picker to show. */}
        {shippingOptions.length <= 1 &&
          calculatedShipping &&
          formatDeliveryEstimate(
            calculatedShipping.minDeliveryDate,
            calculatedShipping.maxDeliveryDate,
          ) && (
            <p className="payment-summary-item text-gray-500 text-sm">
              <span>Estimated Delivery</span>{" "}
              <span>
                {formatDeliveryEstimate(
                  calculatedShipping.minDeliveryDate,
                  calculatedShipping.maxDeliveryDate,
                )}
              </span>
            </p>
          )}
        <p className="payment-summary-item">
          <span>Estimated Sales Tax</span>{" "}
          <span>${(adjustedTaxCents / 100).toFixed(2)}</span>
        </p>
        {isDonationStepEnabled && (
          <p className="payment-summary-item">
            <span>Donation</span>{" "}
            <span>${(donationAmount / 100).toFixed(2).toString()}</span>
          </p>
        )}
      </div>
      <p className="payment-total-amount mt-4">
        Total: ${(grandTotalPrice / 100).toFixed(2).toString()}
      </p>
      <p className="text-xs font-light">
        Taxes finalized at the end of checkout
      </p>
    </>
  );

  if (cart.length === 0) {
    return (
      <div className="payment-container pt-8">
        <div className="text-center py-16">
          <p className="text-gray-500 lora-text text-lg">Your cart is empty.</p>
          <p className="text-gray-400 text-sm mt-1">
            Add some items to your cart before checking out.
          </p>
          <Link to="/home" className="mt-4 inline-block btn-primary text-sm">
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Visible surface for every checkout failure — without it a failed
          payment/fulfillment left the customer on a spinner with no message. */}
      <PaymentErrorToast />

      {isSubmitting && <Spinner text="Processing Payment..." />}

      <ConfirmationModal isOpen={confirmationModelOpen}>
        <PaymentConfirm
          order={order!}
          payment={payment!}
          contactInfo={contactInfo}
          phone={phone ?? ""}
          selectedShipping={calculatedShipping!}
          saveCard={saveCardForOrder}
          accountNotice={
            // Told on the receipt whether or not a card was in play. Gating
            // this on `saveNewCard` meant a buyer who hadn't ticked "save my
            // card" was signed out completely silently: the checkout banner is
            // only reachable if the order FAILS, so on the success path — the
            // common one — nothing anywhere said the session had ended. They'd
            // find out days later, looking for an order that isn't in their
            // history, which reads as "the site lost my order" rather than
            // "sign in again". The card sentence is the conditional part.
            sessionEndedMidCheckout
              ? `We couldn't verify your account while placing this order, so you've been signed out.${
                  saveNewCard ? " Your card wasn't saved." : ""
                } This order is safe and confirmed — sign in again to see it in your order history.`
              : undefined
          }
          onFailure={() => setConfirmationModalOpen(false)}
        />
      </ConfirmationModal>

      {/* Compact summary block — mobile only. The full Order Summary stacks at
          the top of the page, so once the customer scrolls down into the steps
          it's gone; this slides in and pins to the top the moment they pass the
          bottom of the summary, keeping the total in view. Tapping it jumps back
          up to the full summary. Kept mounted and translated off-screen (rather
          than conditionally rendered) so it can animate in/out. */}
      <div
        aria-hidden={!showStickySummary}
        style={{ top: headerOffset }}
        className={`md:hidden fixed inset-x-0 z-40 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur transition-all duration-200 ${
          showStickySummary
            ? "translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0"
        }`}
      >
        <button
          type="button"
          tabIndex={showStickySummary ? 0 : -1}
          aria-expanded={isStickySummaryExpanded}
          onClick={() => setIsStickySummaryExpanded((open) => !open)}
          className="flex w-full items-center justify-between px-4 py-2.5"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <span className="text-xs">
              {isStickySummaryExpanded ? "▲" : "▼"}
            </span>
            {cart.length} Item{cart.length > 1 ? "s" : ""}
          </span>
          <span className="text-base font-bold" translate="no">
            ${(grandTotalPrice / 100).toFixed(2).toString()}
          </span>
        </button>
        {isStickySummaryExpanded && (
          <div
            className="overflow-y-auto border-t border-gray-200 px-4 pb-4"
            style={{ maxHeight: `calc(100vh - ${headerOffset}px - 3rem)` }}
          >
            {orderSummaryDetails}
          </div>
        )}
      </div>

      <div id="payment-form-page" className="payment-container pt-8">
        <div className="payment-checkout-wrapper flex flex-col md:flex-row gap-8">
          {/* Left Section - Order Summary. On desktop `.payment-summary-section`
              is already `sticky top-16` (App.css) so it stays beside the steps;
              on mobile it's static and the compact bar above takes over. */}
          <div
            ref={summarySectionRef}
            className="payment-summary-section md:w-1/2"
          >
            <PaymentHeader
              title="Order Summary"
              subTitle={
                !isOrderSummaryDropdownOpen
                  ? `${cart.length} Item${cart.length > 1 ? "s" : ""}`
                  : undefined
              }
              price={(grandTotalPrice / 100).toFixed(2).toString()}
              dropdownUp={isOrderSummaryDropdownOpen}
              onDropdownClick={() => {
                setIsOrderSummaryDropdownOpen(!isOrderSummaryDropdownOpen);
              }}
            />
            {isOrderSummaryDropdownOpen && orderSummaryDetails}
          </div>

          {/* Right Section - Contact Info & Payment */}
          <form className="payment-form-section md:w-1/2" autoComplete="off">
            <PaymentHeader
              title="Contact Information"
              stepNumber={PaymentStep.ContactInfo}
              isComplete={paymentStep > PaymentStep.ContactInfo}
              // Once this step is saved its fields are unmounted, so the email
              // the buyer typed is otherwise gone from the screen for the rest
              // of checkout. Echo it in the collapsed header — it's the address
              // every order email and the guest order lookup hang off.
              subTitle={
                paymentStep > PaymentStep.ContactInfo
                  ? (contactInfo.buyerEmailAddress ?? undefined)
                  : undefined
              }
              onEdit={() => {
                setPaymentStep(PaymentStep.ContactInfo);
              }}
            />
            {/* Optional inline sign-in / account creation. Above the fields
                because signing in FILLS them — offering it after they've typed
                their address is offering it too late.

                HIDDEN rather than unmounted, and so deliberately outside
                PaymentSection: that renders `{isOpen && children}`, so a closed
                step drops its children's local state. The panel's state
                includes `otpToken` — single-use and 10-minute — so a buyer who
                requested a code, stepped forward, then came back would find the
                panel collapsed and have to burn a second email to get another
                one. `display:none` keeps it out of the layout and the
                accessibility tree while the step is closed. */}
            {/* Outside the step wrapper below, because both sign-outs it
                reports land at the PAYMENT step — hiding it with the Contact
                step would hide it exactly when it fires.

                Two different involuntary sign-outs, two different consequences,
                so they don't share a sentence:

                  sessionEndedMidCheckout — handlePay found the session's Square
                    customer unreachable. Reaching this banner means the attempt
                    then FAILED (handlePay threw, or PaymentConfirm's draft
                    setup did and called onFailure) — on the success path the
                    buyer is on the receipt page and never sees this, which is
                    why the card-save note lives there instead. So the copy must
                    NOT claim the order survived: both failure paths void the
                    authorized payment before landing back here.
                  sessionExpired — the token lapsed (CustomerAuthContext drops
                    it at load, on focus, or on its timer). Nothing has been
                    charged, but the cards on file went with the session: the
                    saved-card list below is simply replaced by the card form,
                    and without this the buyer just finds the card they came to
                    pay with missing.

                Both are involuntary, which is the whole reason they get a
                notice — an explicit "Not you?" sign-out sets neither flag.

                The way back is a BUTTON, not the words "sign in above": this
                banner deliberately outlives the Contact step, but the account
                panel does not (it's display:none from the wrapper below), so at
                the payment step — where an expiry actually bites, because the
                cards on file have just vanished — "above" pointed at nothing on
                screen. */}
            {(sessionEndedMidCheckout || sessionExpired) && (
              <div
                className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-rust bg-cream-light p-3 text-sm text-brown-dark opensans-text"
                role="status"
              >
                <div>
                  {sessionEndedMidCheckout ? (
                    <>
                      We couldn't verify your account, so you've been signed
                      out. Nothing has been charged and your cart is unchanged
                      —{" "}
                    </>
                  ) : (
                    <>
                      Your sign-in session expired, so you've been signed out.
                      Your cart and the details below are unchanged, but any
                      card you had on file isn't available —{" "}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setPaymentStep(PaymentStep.ContactInfo)}
                    className="text-brown hover:text-brown-dark underline"
                  >
                    sign in again at Contact Information
                  </button>
                  {sessionEndedMidCheckout
                    ? " and try again."
                    : ", or enter your card at the payment step."}
                </div>
                {/* Dismissible, because `sessionExpired` lives in the auth
                    CONTEXT and so outlives this page: a buyer who reads it and
                    decides to carry on as a guest would otherwise be told their
                    session expired on every return to checkout for the rest of
                    the visit. `sessionEndedMidCheckout` is local state and dies
                    with the page, but it gets the same control — a notice you
                    can't put down reads as an error the site is stuck on. */}
                <button
                  type="button"
                  onClick={() => {
                    setSessionEndedMidCheckout(false);
                    clearSessionExpired();
                  }}
                  className="text-brown hover:text-brown-dark text-lg leading-none"
                  aria-label="Dismiss this notice"
                >
                  ×
                </button>
              </div>
            )}
            <div
              className={
                paymentStep === PaymentStep.ContactInfo ? undefined : "hidden"
              }
            >
              <CheckoutAccountPanel
                email={contactInfo.buyerEmailAddress ?? ""}
                onSignedIn={applySignedInPrefill}
                onSignedOut={clearSignedOutDetails}
              />
            </div>
            <PaymentSection isOpen={paymentStep === PaymentStep.ContactInfo}>
              <PaymentContactInfo
                details={contactInfo}
                setDetails={setContactInfo}
                phone={phone ?? ""}
                setPhone={
                  setPhone as React.Dispatch<React.SetStateAction<string>>
                }
              />
              <button
                type="button"
                className={`w-full h-10 rounded-lg text-gray-50 text-sm mt-2 ${
                  isContactStepInvalid
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-blue-500"
                }`}
                onClick={async () => {
                  if (isDonationStepEnabled) {
                    setPaymentStep(PaymentStep.Donation);
                  } else {
                    await handleAdvanceToShippingOrPayment();
                  }
                }}
                disabled={isContactStepInvalid || isCalculating}
              >
                {isCalculating ? "Calculating..." : "Save & Continue"}
              </button>
            </PaymentSection>

            {/* Donation Section */}
            {isDonationStepEnabled && (
              <>
                <PaymentHeader
                  title="Donation"
                  stepNumber={PaymentStep.Donation}
                  isComplete={paymentStep > PaymentStep.Donation}
                  onEdit={() => {
                    setPaymentStep(PaymentStep.Donation);
                  }}
                />
                <PaymentSection isOpen={paymentStep === PaymentStep.Donation}>
                  <h1>Would you like to donate to a very noble cause?</h1>
                  <p>
                    At Americana & Company, we care about community and think
                    there is nothing more valuable and American than to
                    contribute to our fellow citizens in need. By checking the
                    box below we will round up total price to the nearest number
                    and donate that extra amount to {`<Insert Charity Here>`}.
                    Thank you so much in advance for your kindness!
                  </p>
                  <div className="mt-4 flex items-center">
                    <StyledCheckbox
                      id="donation"
                      checked={isDonating}
                      onChange={() => setIsDonating(!isDonating)}
                      label="Yes, I would like to donate by rounding up my total!"
                    />
                  </div>
                  <button
                    type="button"
                    className={`w-full h-10 rounded-lg text-gray-50 text-sm mt-2 ${
                      isFormInvalid
                        ? "cursor-not-allowed bg-gray-400"
                        : "bg-blue-500"
                    }`}
                    onClick={async () => {
                      await handleAdvanceToShippingOrPayment();
                    }}
                    disabled={isCalculating}
                  >
                    {isCalculating ? "Calculating..." : "Save & Continue"}
                  </button>
                </PaymentSection>
              </>
            )}

            {/* Shipping Method Section — always its own step (all three step
                headers show from the start), so mobile customers pick a rate
                inside the flow rather than up in the far-scrolled summary. */}
            <PaymentHeader
              title="Shipping Method"
              stepNumber={shippingStepNumber}
              isComplete={paymentStep > PaymentStep.Shipping}
              onEdit={
                paymentStep > PaymentStep.Shipping
                  ? () => setPaymentStep(PaymentStep.Shipping)
                  : undefined
              }
            />
            <PaymentSection isOpen={paymentStep === PaymentStep.Shipping}>
              <fieldset>
                <legend className="sr-only">Shipping Method</legend>
                <div className="flex flex-col gap-2">
                  {shippingOptions.map((option) => {
                    const estimate = formatDeliveryEstimate(
                      option.minDeliveryDate,
                      option.maxDeliveryDate,
                    );
                    const isSelected = option.id === calculatedShipping?.id;
                    return (
                      <label
                        key={option.id}
                        className={`flex items-center gap-3 rounded border p-3 cursor-pointer ${
                          isSelected
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="shippingMethod"
                          value={option.id}
                          checked={isSelected}
                          disabled={isRecalculatingTax}
                          onChange={() => handleSelectShipping(option)}
                        />
                        <span className="flex-1 text-sm">
                          <span className="font-medium">
                            {shippingMethodLabel(option)}
                          </span>
                          {estimate && (
                            <span className="block text-gray-500 text-xs">
                              Estimated delivery {estimate}
                            </span>
                          )}
                        </span>
                        <span className="text-sm font-medium">
                          ${Number(option.rate).toFixed(2)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <button
                type="button"
                className={`w-full h-10 rounded-lg text-gray-50 text-sm mt-4 ${
                  isRecalculatingTax
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-blue-500"
                }`}
                onClick={() => setPaymentStep(PaymentStep.Payment)}
                disabled={isRecalculatingTax}
              >
                {isRecalculatingTax ? "Updating..." : "Continue to Payment"}
              </button>
            </PaymentSection>

            {/* Payment Section */}
            <PaymentHeader
              title="Payment Information"
              stepNumber={paymentStepNumber}
              isComplete={paymentStep > PaymentStep.Payment}
              // onEdit={() => {
              //   setPaymentStep(PaymentStep.Payment);
              // }}
            />
            <PaymentSection isOpen={paymentStep === PaymentStep.Payment}>
              <div className="flex flex-col gap-6">
                {/* Terms and Agreements and Privacy Policy */}
                <TermsAndAgreementsModal isDisclaimerEnabled />

                {/* Where the order's mail is going, restated at the moment of
                    payment. The Contact step's fields are UNMOUNTED by now
                    (PaymentSection renders `{isOpen && children}`) and its
                    collapsed header shows only a tick, so without this the
                    buyer cannot see the address they typed anywhere on the
                    page — which is why "I don't think I ever entered an email"
                    survives all the way to the receipt. */}
                {contactInfo.buyerEmailAddress && (
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-gray-600">
                    <span>
                      Your confirmation and tracking emails go to{" "}
                      {/* An address is data, not prose — a page translation
                          must not rewrite it (see docs/browser-translation). */}
                      <span className="font-medium" translate="no">
                        {contactInfo.buyerEmailAddress}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="underline hover:text-brown-dark"
                      onClick={() => setPaymentStep(PaymentStep.ContactInfo)}
                    >
                      Change
                    </button>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {/* Apple Pay / Google Pay — custom buttons (see
                      WalletButtons.tsx) so wallet failures are visible and the
                      sheet total is rebuilt whenever the order total changes.
                      Wallet tokens skip verifyBuyer by design.

                      The heading and the divider below it are not decoration.
                      Unlabelled, Google's black "Buy with G Pay" button sat
                      flush on top of the card form and read as part of it: a
                      customer reaching for the card-number box to trigger her
                      browser's autofill clicked the wallet instead, and Google
                      Pay's sheet looks enough like Chrome's own saved-card
                      popup that she completed the payment still believing she
                      was typing a card in. Say plainly that this is a separate
                      way to pay, and that the card form below is the other. */}
                  {walletAvailable && (
                    <h3 className="text-sm font-medium text-gray-700">
                      Express checkout
                    </h3>
                  )}
                  {squareKeys && (
                    <WalletButtons
                      applicationId={squareKeys.applicationId}
                      locationId={squareKeys.locationId}
                      paymentRequest={createPaymentRequest()}
                      onToken={async (token) => await handlePay(token)}
                      disabled={isSubmitting}
                      onAvailabilityChange={setWalletAvailable}
                    />
                  )}
                  {walletAvailable && (
                    <div
                      className="flex items-center gap-3 my-1 text-xs text-gray-500"
                      aria-hidden="true"
                    >
                      <span className="h-px flex-1 bg-gray-300" />
                      <span>or pay with a card</span>
                      <span className="h-px flex-1 bg-gray-300" />
                    </div>
                  )}

                  {/* Cards on file — signed-in customers only, since a stored
                      card hangs off a Square customer profile. Rendered above
                      the card form so the fast path is the first thing seen. */}
                  {savedCards.length > 0 && (
                    <fieldset className="flex flex-col gap-2">
                      <legend className="text-sm font-medium mb-1">
                        Your saved cards
                      </legend>
                      {savedCards.map((card) => {
                        const isSelected = card.id === selectedCardId;
                        const expiry = savedCardExpiry(card);
                        return (
                          <label
                            key={card.id}
                            className={`flex items-center gap-3 rounded border p-3 cursor-pointer ${
                              isSelected
                                ? "border-blue-600 bg-blue-50"
                                : "border-gray-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="savedCard"
                              value={card.id}
                              checked={isSelected}
                              disabled={isSubmitting}
                              onChange={() => setSelectedCardId(card.id)}
                            />
                            <span className="flex-1 text-sm">
                              {/* Card digits are data, not prose — a page
                                  translation must not rewrite them. */}
                              <span className="font-medium" translate="no">
                                {savedCardLabel(card)}
                              </span>
                              {expiry && (
                                <span
                                  className="block text-gray-500 text-xs"
                                  translate="no"
                                >
                                  Expires {expiry}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                      <label
                        className={`flex items-center gap-3 rounded border p-3 cursor-pointer ${
                          selectedCardId === null
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="savedCard"
                          value=""
                          checked={selectedCardId === null}
                          disabled={isSubmitting}
                          onChange={() => setSelectedCardId(null)}
                        />
                        <span className="flex-1 text-sm font-medium">
                          Use a different card
                        </span>
                      </label>
                    </fieldset>
                  )}

                  {selectedCardId ? (
                    /* Paying with a card on file: no card fields to render, and
                       no tokenization step — the server resolves the card id
                       after verifying it belongs to this customer. */
                    <button
                      type="button"
                      className="w-full h-10 rounded-lg text-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                      onClick={async () =>
                        await handlePay(null, null, selectedCardId)
                      }
                    >
                      {isSubmitting
                        ? "Processing..."
                        : `Pay $${(grandTotalPrice / 100).toFixed(2)}`}
                    </button>
                  ) : (
                    <>
                      {/* Consent to store the card, above the form because
                          <CreditCard> renders its own Pay button at the bottom —
                          a checkbox below that would be read after the fact.
                          Unchecked by default; see saveNewCard. */}
                      {session ? (
                        <div className="flex items-center">
                          <StyledCheckbox
                            id="save-card"
                            checked={saveNewCard}
                            onChange={() => setSaveNewCard(!saveNewCard)}
                            label="Save my information for a faster checkout next time"
                          />
                        </div>
                      ) : (
                        /* A card on file hangs off a customer profile, so
                           there's nothing to save it to as a guest. The account
                           panel lives at the Contact step (it has to — signing
                           in FILLS those fields), so this can only send them
                           back there.

                           That trip is not free, and the label has to say so:
                           PaymentSection renders `{isOpen && children}`, so
                           leaving this step UNMOUNTS the Square card iframe and
                           any digits already typed into it go with it. The
                           iframe is cross-origin and the SDK exposes no dirty
                           flag, so there's nothing to detect here and no way to
                           warn only the buyers it affects — which leaves saying
                           it up front, before the click, for everyone. A
                           silently emptied card form reads as the site
                           breaking, not as the cost of a choice they made.

                           Rendered above <PaymentForm> for the same reason the
                           save-card checkbox is: <CreditCard> puts its own Pay
                           button at the bottom, so anything below it is read
                           after the fact. Above the form, "you'll re-enter your
                           card" is a warning; below it, it's an apology. */
                        <button
                          type="button"
                          onClick={() =>
                            setPaymentStep(PaymentStep.ContactInfo)
                          }
                          className="text-xs text-gray-500 hover:text-brown-dark underline text-left"
                        >
                          Want a faster checkout next time? Create an account or
                          sign in at Contact Information to save your card —
                          you'll come back here to re-enter your card details.
                        </button>
                      )}

                      {/* Card entry keeps react-square-web-payments-sdk and its
                          verifyBuyer (SCA) flow via createVerificationDetails. */}
                      <PaymentForm
                        applicationId={squareKeys?.applicationId ?? ""}
                        locationId={squareKeys?.locationId ?? ""}
                        cardTokenizeResponseReceived={async (
                          token,
                          verifiedBuyer,
                        ) => await handlePay(token, verifiedBuyer)}
                        createVerificationDetails={getVerificationDetails}
                      >
                        <CreditCard buttonProps={buttonProps} />
                      </PaymentForm>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="w-full bg-red-700 h-10 rounded-lg text-gray-50 text-sm mt-2"
                onClick={() => {
                  navigate("/home");
                }}
              >
                Cancel
              </button>
            </PaymentSection>
          </form>
        </div>
      </div>
    </>
  );
};

export default PaymentFormPage;
